/**
 * LPA Factory Settings Page
 *
 * Professional settings form for per-factory LPA configuration.
 * Organized into three logical sections:
 *   1. Schedule — week start day, end-of-business hour, generation lead days
 *   2. Notifications — reminder timing before due audits
 *   3. Escalation — escalation timing after missed audits
 *
 * The form uses an upsert pattern: if no settings row exists yet, the UI shows
 * DB-column defaults and creates the row on first save.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Separator } from '@/shared/ui/separator';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  CalendarClock,
  Clock,
  Bell,
  AlertTriangle,
  Info,
  Save,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { useFactorySettings, useUpsertFactorySettings, FACTORY_SETTINGS_DEFAULTS } from '../hooks/useFactorySettings';
import type { LpaFactorySettings } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WEEK_DAYS = [
  { value: '0', labelKey: 'mod_lpa.settings.day.sunday', fallback: 'Sunday' },
  { value: '1', labelKey: 'mod_lpa.settings.day.monday', fallback: 'Monday' },
  { value: '2', labelKey: 'mod_lpa.settings.day.tuesday', fallback: 'Tuesday' },
  { value: '3', labelKey: 'mod_lpa.settings.day.wednesday', fallback: 'Wednesday' },
  { value: '4', labelKey: 'mod_lpa.settings.day.thursday', fallback: 'Thursday' },
  { value: '5', labelKey: 'mod_lpa.settings.day.friday', fallback: 'Friday' },
  { value: '6', labelKey: 'mod_lpa.settings.day.saturday', fallback: 'Saturday' },
] as const;

/** Format an hour (0-23) as "HH:00" for display. */
function formatHour(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`;
}

// ---------------------------------------------------------------------------
// Helper: Info label with tooltip
// ---------------------------------------------------------------------------

function FieldLabel({
  htmlFor,
  label,
  tooltip,
}: {
  htmlFor: string;
  label: string;
  tooltip: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </Label>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type FormState = {
  week_start_day: number;
  eob_hour: number;
  schedule_generation_lead_days: number;
  reminder_hours_before: number;
  escalation_hours_after: number;
};

function settingsToForm(
  settings: LpaFactorySettings | null | undefined,
): FormState {
  return {
    week_start_day: settings?.week_start_day ?? FACTORY_SETTINGS_DEFAULTS.week_start_day,
    eob_hour: settings?.eob_hour ?? FACTORY_SETTINGS_DEFAULTS.eob_hour,
    schedule_generation_lead_days:
      settings?.schedule_generation_lead_days ??
      FACTORY_SETTINGS_DEFAULTS.schedule_generation_lead_days,
    reminder_hours_before:
      settings?.reminder_hours_before ?? FACTORY_SETTINGS_DEFAULTS.reminder_hours_before,
    escalation_hours_after:
      settings?.escalation_hours_after ?? FACTORY_SETTINGS_DEFAULTS.escalation_hours_after,
  };
}

export default function FactorySettingsPage() {
  const { t } = useLanguage();
  const { data: settings, isLoading } = useFactorySettings();
  const upsertMutation = useUpsertFactorySettings();

  const [form, setForm] = useState<FormState>(settingsToForm(null));

  // Sync form when server data arrives or changes
  useEffect(() => {
    if (!isLoading) {
      setForm(settingsToForm(settings));
    }
  }, [settings, isLoading]);

  // Dirty tracking
  const serverForm = settingsToForm(settings);
  const isDirty =
    form.week_start_day !== serverForm.week_start_day ||
    form.eob_hour !== serverForm.eob_hour ||
    form.schedule_generation_lead_days !== serverForm.schedule_generation_lead_days ||
    form.reminder_hours_before !== serverForm.reminder_hours_before ||
    form.escalation_hours_after !== serverForm.escalation_hours_after;

  const handleReset = useCallback(() => {
    setForm(settingsToForm(settings));
  }, [settings]);

  const handleSave = useCallback(() => {
    upsertMutation.mutate({
      week_start_day: form.week_start_day,
      eob_hour: form.eob_hour,
      schedule_generation_lead_days: form.schedule_generation_lead_days,
      reminder_hours_before: form.reminder_hours_before,
      escalation_hours_after: form.escalation_hours_after,
    });
  }, [form, upsertMutation]);

  // Field updater
  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Loading skeleton
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-7 w-48 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-72" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {t('mod_lpa.settings.title', 'LPA Settings')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'mod_lpa.settings.description',
              'Configure factory-specific LPA audit settings.',
            )}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty || upsertMutation.isPending}
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            {t('mod_lpa.settings.reset', 'Reset')}
          </Button>
          <Button
            size="sm"
            disabled={!isDirty || upsertMutation.isPending}
            onClick={handleSave}
          >
            {upsertMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1.5" />
            )}
            {t('mod_lpa.settings.save', 'Save changes')}
          </Button>
        </div>
      </div>

      {!settings && (
        <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
          {t(
            'mod_lpa.settings.defaultsHint',
            'No custom settings saved yet for this factory. The values below are system defaults. Adjust and save to customize.',
          )}
        </p>
      )}

      {/* ── Section 1: Schedule ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">
              {t('mod_lpa.settings.schedule.title', 'Schedule Configuration')}
            </CardTitle>
          </div>
          <CardDescription>
            {t(
              'mod_lpa.settings.schedule.description',
              'Control how audit schedules are generated and when work weeks start.',
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Week start day */}
          <div className="grid grid-cols-1 sm:grid-cols-[280px_1fr] items-start gap-2 sm:gap-6">
            <FieldLabel
              htmlFor="week-start-day"
              label={t('mod_lpa.settings.weekStartDay', 'Week start day')}
              tooltip={t(
                'mod_lpa.settings.weekStartDayTooltip',
                'The first day of the audit week. Weekly audits will be scheduled starting from this day.',
              )}
            />
            <Select
              value={String(form.week_start_day)}
              onValueChange={(v) => setField('week_start_day', Number(v))}
            >
              <SelectTrigger id="week-start-day" className="w-full sm:max-w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEK_DAYS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey, d.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* End-of-business hour */}
          <div className="grid grid-cols-1 sm:grid-cols-[280px_1fr] items-start gap-2 sm:gap-6">
            <FieldLabel
              htmlFor="eob-hour"
              label={t('mod_lpa.settings.eobHour', 'End-of-business hour')}
              tooltip={t(
                'mod_lpa.settings.eobHourTooltip',
                'Daily audit windows close at this hour (24h format). Audits scheduled for the day must be completed before this time to count as on-time.',
              )}
            />
            <div className="flex items-center gap-3">
              <Select
                value={String(form.eob_hour)}
                onValueChange={(v) => setField('eob_hour', Number(v))}
              >
                <SelectTrigger id="eob-hour" className="w-full sm:max-w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {formatHour(i)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Clock className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
            </div>
          </div>

          <Separator />

          {/* Schedule generation lead days */}
          <div className="grid grid-cols-1 sm:grid-cols-[280px_1fr] items-start gap-2 sm:gap-6">
            <FieldLabel
              htmlFor="lead-days"
              label={t('mod_lpa.settings.leadDays', 'Generation lead days')}
              tooltip={t(
                'mod_lpa.settings.leadDaysTooltip',
                'How many days in advance individual audit instances are generated from recurring schedules. A higher value gives auditors more visibility into upcoming audits.',
              )}
            />
            <div className="flex items-center gap-2">
              <Input
                id="lead-days"
                type="number"
                min={1}
                max={90}
                className="w-full sm:max-w-[120px]"
                value={form.schedule_generation_lead_days}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 90) {
                    setField('schedule_generation_lead_days', v);
                  }
                }}
              />
              <span className="text-sm text-muted-foreground">
                {t('mod_lpa.settings.days', 'days')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Section 2: Notifications ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">
              {t('mod_lpa.settings.notifications.title', 'Reminders')}
            </CardTitle>
          </div>
          <CardDescription>
            {t(
              'mod_lpa.settings.notifications.description',
              'Configure when auditors receive reminders about upcoming audit instances.',
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-[280px_1fr] items-start gap-2 sm:gap-6">
            <FieldLabel
              htmlFor="reminder-hours"
              label={t('mod_lpa.settings.reminderHours', 'Reminder lead time')}
              tooltip={t(
                'mod_lpa.settings.reminderHoursTooltip',
                'Hours before an audit is due when a reminder notification is sent to the assigned auditor. Set to 0 to disable reminders.',
              )}
            />
            <div className="flex items-center gap-2">
              <Input
                id="reminder-hours"
                type="number"
                min={0}
                max={168}
                className="w-full sm:max-w-[120px]"
                value={form.reminder_hours_before}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 0 && v <= 168) {
                    setField('reminder_hours_before', v);
                  }
                }}
              />
              <span className="text-sm text-muted-foreground">
                {t('mod_lpa.settings.hours', 'hours')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Section 3: Escalation ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-base">
              {t('mod_lpa.settings.escalation.title', 'Escalation')}
            </CardTitle>
          </div>
          <CardDescription>
            {t(
              'mod_lpa.settings.escalation.description',
              'Define when overdue audits trigger escalation to supervisors.',
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-[280px_1fr] items-start gap-2 sm:gap-6">
            <FieldLabel
              htmlFor="escalation-hours"
              label={t('mod_lpa.settings.escalationHours', 'Escalation delay')}
              tooltip={t(
                'mod_lpa.settings.escalationHoursTooltip',
                'Hours after an audit becomes overdue before an escalation notification is sent to the responsible supervisor. Helps ensure missed audits are acted on promptly.',
              )}
            />
            <div className="flex items-center gap-2">
              <Input
                id="escalation-hours"
                type="number"
                min={1}
                max={168}
                className="w-full sm:max-w-[120px]"
                value={form.escalation_hours_after}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 168) {
                    setField('escalation_hours_after', v);
                  }
                }}
              />
              <span className="text-sm text-muted-foreground">
                {t('mod_lpa.settings.hours', 'hours')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
